---
meta:
  - title: Revisar documentos por lote - IUSPRO Docs
  - description: Aprende a revisar múltiples documentos al mismo tiempo.
---

# Revisar documentos por lote

La función de lotes le permite revisar muchos documentos a la vez y organizarlos para encontrar rápidamente los que tienen problemas.

## ¿Qué es un lote?

Un lote es una etiqueta que agrupa varios documentos revisados juntos. Por ejemplo:

- "Lote 19 de noviembre 2025 - Contratos de Pérez"
- "Ejecución mensual - Octubre 2025"
- "Casos pendientes - Semana 42"

## Como crear una ejecución por lote

### Paso 1: Nueva ejecución

1. **Vaya a "Playbooks" y abra "Ejecuciones"**
2. **Haga clic en "Nueva ejecución"**
3. **Seleccione el playbook** que quiere usar

### Paso 2: Subir documentos

Puede subir múltiples documentos:

- **Desde 1 hasta 1000 documentos**
- Puede subirlos todos a la vez

### Paso 3: Asignar etiqueta de lote

Dele un nombre al lote, por ejemplo:

```
"Lote 19 de noviembre 2025 - Contratos de Pérez"
```

Esta etiqueta le ayudará a encontrar estos documentos después.

### Paso 4: Procesar

El sistema revisará todos los documentos automáticamente usando el playbook que seleccionó.

## Ver resultados del lote

Una vez procesados, en la sección de Ejecuciones verá:

### Vista de lista

Cada documento muestra un resumen rápido:

- **✅ 14 reglas cumplidas**
- **⚠️ 2 reglas con alternativa aceptable**
- **❌ 7 reglas no cumplidas**

### Filtrar por lote

En la parte derecha de la pantalla:

1. **Haga clic en filtrar**
2. **Seleccione el lote** que quiere ver
3. Verá solo los documentos de ese lote

### Filtrar por estado

Puede filtrar para ver:

- Solo documentos con reglas no cumplidas
- Solo documentos perfectos
- Documentos con alternativas

## Estrategia de ejecución eficiente

### Enfóquese en los problemas

1. **Filtre por su lote**
2. **Ordene por cantidad de reglas no cumplidas**
3. **Revise primero los documentos con más problemas**

Por ejemplo, si un documento muestra:

```
❌ 7 reglas no cumplidas
```

Ese documento necesita más atención que uno con solo 1 regla no cumplida.

### Ver detalles de cada documento

1. **Haga clic en un documento**
2. **Verá todas las reglas** y su estado
3. **Haga clic en una regla no cumplida**
4. **El sistema le muestra**:
   - Por qué no se cumplió
   - En qué parte del texto debería estar
   - Qué falta o está mal

## Ejemplo de uso real

### Revisar 100 contratos

**Situación**: Tiene 100 contratos de empleados que necesita revisar.

**Proceso**:

1. Crea un playbook "Requisitos contratos laborales" (una sola vez)
2. Sube los 100 contratos
3. Les pone etiqueta "Contratos laborales - Enero 2025"
4. El sistema los revisa todos automáticamente
5. Ve el resumen:
   - 85 contratos perfectos
   - 12 contratos con problemas menores
   - 3 contratos con problemas graves
6. Se enfoca en revisar solo los 15 contratos con problemas

**Resultado**: En lugar de revisar 100 contratos completos manualmente, solo revisa 15 con problemas. Ahorra días de trabajo.

## Múltiples lotes

Puede tener múltiples lotes activos:

- "Contratos laborales - Enero 2025"
- "Contratos laborales - Febrero 2025"
- "Contratos de servicios - Q1 2025"

Cada uno lo puede filtrar independientemente.

## Ventajas de trabajar por lotes

- **Organización**: Todo está agrupado lógicamente
- **Eficiencia**: Procesa muchos documentos a la vez
- **Priorización**: Ve rápidamente cuáles necesitan atención
- **Trazabilidad**: Sabe exactamente qué documentos revisó y cuándo
